import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToasterService } from '../../../service/common/toaster.service';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';

import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import {
  PublicPatientRegistrationService,
  PatientLookupResult,
} from '../../../service/public-patient-registration.service';
import {
  finalizePatientAgeInput,
  getPatientAgeError,
  isValidPatientAge,
  normalizePatientAgeForStorage,
  normalizePatientAgeInput,
  PATIENT_AGE_HINT,
} from '../../../shared/patient-age.util';

type Step = 'lookup' | 'found' | 'register' | 'success';

interface ProgressStep {
  label: string;
  icon: string;
}

@Component({
  selector: 'app-customer-patient-registration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    InputTextareaModule,
  ],
  templateUrl: './customer-patient-registration.component.html',
  styleUrls: ['./customer-patient-registration.component.scss'],
  animations: [
    trigger('stepFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(16px)' }),
        animate('320ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class CustomerPatientRegistrationComponent {
  // logoUrl = '/logo.png'; // local
  readonly logoUrl = '/clinic/logo.png'; // production
  // doctorFigureUrl = '/drsancheti.png'; // local
  readonly doctorFigureUrl = '/clinic/drsancheti.png'; // production

  step: Step = 'lookup';
  lookupLoading = false;
  submitLoading = false;
  lookupResult: PatientLookupResult | null = null;

  readonly progressSteps: ProgressStep[] = [
    { label: 'Verify', icon: 'pi-search' },
    { label: 'Your details', icon: 'pi-user-edit' },
    { label: 'Complete', icon: 'pi-check' },
  ];

  readonly bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  readonly genderOptions = [
    { value: 'Male', icon: 'pi-user' },
    { value: 'Female', icon: 'pi-user' },
    { value: 'Other', icon: 'pi-users' },
  ];

  lookup = {
    first_name: '',
    last_name: '',
    primary_contact: '',
    website: '',
  };

  form = {
    first_name: '',
    middle_name: '',
    last_name: '',
    primary_contact: '',
    age: null as number | null,
    gender: '',
    blood_group: '',
    address: '',
    city: '',
    clinic_id: 1,
    website: '',
  };

  referenceId: number | null = null;

  readonly patientAgeHint = PATIENT_AGE_HINT;
  readonly isValidPatientAge = isValidPatientAge;
  patientAgeError: string | null = null;

  readonly currentYear = new Date().getFullYear();

  get showDoctorHero(): boolean {
    return true;
  }

  get doctorHeroTone(): 'default' | 'success' | 'found' | 'pending' {
    if (this.step === 'success') return 'success';
    if (this.step === 'found' && this.foundMode === 'registered') return 'found';
    if (this.step === 'found' && this.foundMode === 'pending') return 'pending';
    return 'default';
  }

  get doctorHeroEyebrow(): string {
    if (this.step === 'success') return 'Registration complete';
    if (this.step === 'register') return 'Step 2 of 3';
    if (this.step === 'found' && this.foundMode === 'registered') return 'Welcome back';
    if (this.step === 'found' && this.foundMode === 'pending') return 'Under review';
    return 'Patient self-registration';
  }

  get doctorHeroTitle(): string {
    if (this.step === 'success') return "You're all set!";
    if (this.step === 'register') return 'Tell us about you';
    if (this.step === 'found' && this.foundMode === 'registered') return 'We found your record';
    if (this.step === 'found' && this.foundMode === 'pending') return 'Review in progress';
    return 'Register before your visit';
  }

  get doctorHeroIcon(): string {
    if (this.step === 'success') return 'pi pi-check-circle';
    if (this.step === 'register') return 'pi pi-user-edit';
    if (this.step === 'found' && this.foundMode === 'registered') return 'pi pi-heart';
    if (this.step === 'found' && this.foundMode === 'pending') return 'pi pi-hourglass';
    return 'pi pi-sparkles';
  }

  get showDoctorHeroTrust(): boolean {
    return this.step === 'lookup' || this.step === 'register';
  }

  get doctorHeroMessage(): string {
    if (this.step === 'success') {
      return 'Show your reference number at reception on your next visit.';
    }
    if (this.step === 'register') {
      return 'A few more details and your profile goes to our team for review.';
    }
    if (this.step === 'found' && this.foundMode === 'registered') {
      return 'Great news — you are already registered with our clinic.';
    }
    if (this.step === 'found' && this.foundMode === 'pending') {
      return 'Our staff is reviewing your details. We will activate your profile soon.';
    }
    return 'Verify your mobile number — it only takes about two minutes.';
  }

  constructor(
    private regService: PublicPatientRegistrationService,
    private toast: ToasterService
  ) {}

  get activeProgressIndex(): number {
    if (this.step === 'register') return 1;
    if (this.step === 'success') return 2;
    return 0;
  }

  get foundMode(): 'registered' | 'pending' | null {
    if (this.step !== 'found' || !this.lookupResult) return null;
    if (this.lookupResult.found) return 'registered';
    if (this.lookupResult.pending) return 'pending';
    return null;
  }

  get lookupDisplayName(): string {
    const first = this.lookup.first_name?.trim();
    const last = this.lookup.last_name?.trim();
    return [first, last].filter(Boolean).join(' ');
  }

  selectGender(value: string) {
    this.form.gender = value;
  }

  selectBloodGroup(bg: string) {
    this.form.blood_group = this.form.blood_group === bg ? '' : bg;
  }

  copyPatientId(id: string) {
    const text = String(id ?? '').trim();
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () =>
        this.toast.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Patient ID copied to clipboard.',
          life: 2500,
        }),
      () =>
        this.toast.add({
          severity: 'info',
          summary: 'Patient ID',
          detail: text,
          life: 4000,
        })
    );
  }

  copyReferenceId() {
    if (!this.referenceId) return;
    const text = `#${this.referenceId}`;
    navigator.clipboard?.writeText(text).then(
      () =>
        this.toast.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Reference number copied.',
          life: 2500,
        }),
      () =>
        this.toast.add({
          severity: 'info',
          summary: 'Reference',
          detail: text,
          life: 4000,
        })
    );
  }

  private launchConfetti() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const colors = ['#0d9488', '#14b8a6', '#10b981', '#34d399', '#5eead4'];
    const duration = 1350;
    const end = Date.now() + duration;

    const canvas = document.createElement('canvas');
    Object.assign(canvas.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '99999',
    } as Partial<CSSStyleDeclaration>);

    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d', { alpha: true })!;
    let w = window.innerWidth;
    let h = window.innerHeight;
    canvas.width = w;
    canvas.height = h;

    const particles: Array<{
      x: number; y: number; size: number; speed: number;
      angle: number; spin: number; color: string; tilt: number;
    }> = [];

    const count = 85;
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * (h * 0.55) - 40,
        size: Math.random() * 6.5 + 4.5,
        speed: Math.random() * 2.2 + 1.8,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.18,
        color: colors[Math.floor(Math.random() * colors.length)],
        tilt: Math.random() * 12 - 6,
      });
    }

    const frame = () => {
      ctx.clearRect(0, 0, w, h);

      let alive = false;

      for (const p of particles) {
        p.y += p.speed;
        p.x += Math.sin(p.angle) * 0.9;
        p.angle += p.spin;
        p.tilt += 0.09;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.tilt);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.65);
        ctx.restore();

        if (p.y < h + 30) alive = true;
      }

      if (Date.now() < end && alive) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    };

    requestAnimationFrame(frame);

    const handleResize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
    };
    window.addEventListener('resize', handleResize, { once: true });
  }

  onLookup() {
    if (!this.lookup.first_name?.trim() || !this.lookup.last_name?.trim()) {
      this.toast.add({
        severity: 'warn',
        summary: 'Name required',
        detail: 'Please enter your first and last name.',
      });
      return;
    }

    const contact = this.lookup.primary_contact?.replace(/\D/g, '');
    if (!contact || contact.length !== 10) {
      this.toast.add({
        severity: 'warn',
        summary: 'Mobile required',
        detail: 'Please enter a valid 10-digit mobile number.',
      });
      return;
    }

    this.lookupLoading = true;
    this.lookupResult = null;
    this.regService
      .lookup({
        first_name: this.lookup.first_name.trim(),
        last_name: this.lookup.last_name.trim(),
        primary_contact: contact,
        website: this.lookup.website,
      })
      .subscribe({
        next: (res) => {
          this.lookupLoading = false;
          this.lookupResult = res;
          if (res.found || res.pending) {
            this.step = 'found';
            if (res.found) {
              setTimeout(() => this.launchConfetti(), 160);
            }
          } else {
            this.form.first_name = this.lookup.first_name.trim();
            this.form.last_name = this.lookup.last_name.trim();
            this.form.primary_contact = contact;
            this.step = 'register';
          }
        },
        error: (err) => {
          this.lookupLoading = false;
          this.toast.add({
            severity: 'error',
            summary: 'Could not verify',
            detail: err?.error?.message || 'Please check your details and try again.',
          });
        },
      });
  }

  goBackFromRegister() {
    this.step = 'lookup';
    this.lookupResult = null;
  }

  startOver() {
    this.step = 'lookup';
    this.lookupResult = null;
    this.referenceId = null;
    this.lookup = {
      first_name: '',
      last_name: '',
      primary_contact: '',
      website: '',
    };
    this.form = {
      first_name: '',
      middle_name: '',
      last_name: '',
      primary_contact: '',
      age: null,
      gender: '',
      blood_group: '',
      address: '',
      city: '',
      clinic_id: 1,
      website: '',
    };
  }

  submitRegistration() {
    if (!this.form.first_name?.trim() || !this.form.last_name?.trim() || !this.form.gender || !this.form.age) {
      this.toast.add({
        severity: 'warn',
        summary: 'Incomplete form',
        detail: 'Please fill first name, last name, age and gender.',
      });
      return;
    }

    this.form.age = finalizePatientAgeInput(this.form.age) as number | null;
    this.patientAgeError = getPatientAgeError(this.form.age);

    if (!isValidPatientAge(this.form.age)) {
      this.toast.add({
        severity: 'warn',
        summary: 'Invalid age',
        detail: getPatientAgeError(this.form.age) || 'Please enter a valid age.',
      });
      return;
    }

    this.submitLoading = true;
    this.regService
      .submit({
        first_name: this.form.first_name.trim(),
        middle_name: this.form.middle_name?.trim() || undefined,
        last_name: this.form.last_name.trim(),
        primary_contact: this.form.primary_contact,
        age: normalizePatientAgeForStorage(this.form.age) ?? Number(this.form.age),
        gender: this.form.gender,
        blood_group: this.form.blood_group || undefined,
        address: this.form.address?.trim() || undefined,
        city: this.form.city?.trim() || undefined,
        clinic_id: 1,
        website: this.form.website,
      })
      .subscribe({
        next: (res) => {
          this.submitLoading = false;
          this.referenceId = res.data?.reference_id ?? null;
          this.step = 'success';
        },
        error: (err) => {
          this.submitLoading = false;
          this.toast.add({
            severity: 'error',
            summary: 'Submission failed',
            detail: err?.error?.message || 'Could not submit registration. Please try again.',
          });
        },
      });
  }

  onPatientAgeChange(value: string | number | null): void {
    this.form.age = normalizePatientAgeInput(value) as number | null;
    this.patientAgeError = getPatientAgeError(this.form.age);
  }

  onPatientAgeBlur(): void {
    this.form.age = finalizePatientAgeInput(this.form.age) as number | null;
    this.patientAgeError = getPatientAgeError(this.form.age);
  }
}
